import React, { useCallback, useMemo, useState } from "react";
import { CircularProgress, Modal } from "@mui/material";
import { useForm } from "react-hook-form";
import { yupResolver } from "@hookform/resolvers/yup";
import * as Yup from "yup";

import { actions } from "../../../redux/store/store";
import { checkForError } from "../../../constants/extras/errorHandlers";
import { useUpdateDriverMutation } from "../../../api/drivers";
import { sendSuccessMessage } from "../../../constants/extras/sendSuccessMsg";
import { InputItem } from "../Form";
import { zipcode } from "../../../redux/constants/zipcode";

const requiredMessage = "This field is required";

const getNameValidationSchema = (title) =>
    Yup.object().shape({
        internalNotes: Yup.string()
            .trim()
            .required(requiredMessage)
            .test(
                "min-letters",
                `${title} must contain at least 2 letters`,
                (value) => {
                    if (!value) return false;
                    const letters = (value.match(/[a-zA-Z]/g) || []).length;
                    return letters >= 2;
                }
            ),
    });

const NotesModal = (props) => {
    const title = props?.id?.title
    const state = props?.id?.state?.state
    const driverId = props?.id?.id;
    const initialNotes = props?.id?.notes || "";

    const isNameField = title === "First Name" || title === "Last Name";

    const [updateDriverMetro, { isLoading: isUpdatingDriver }] =
        useUpdateDriverMutation();
    const [internalNotes, setInternalNotes] = useState(initialNotes);

    const schema = useMemo(() => {
        if (isNameField) {
            return getNameValidationSchema(title);
        }
        return Yup.object().shape({});
    }, [isNameField, title]);

    const form = useForm({
        mode: "onChange",
        defaultValues: {
            internalNotes: initialNotes,
        },
        resolver: isNameField ? yupResolver(schema) : undefined,
    });

    const zipCodeValidation = (data) => {
        const text = data.replace(/[^0-9]/g, '');

        const stateVal = zipcode.find((e) => e.state === state)

        const endZip = stateVal?.ezip
        const startZip = stateVal?.szip

            if (endZip) {
            if (Number(startZip) <= text && Number(endZip) >= text) {
                return true;
            } else {
                return false;
            }
        } else {
            const num = Number(startZip) === text ? false : true;
            return num;
            }
    }

    const onNotesSubmit = useCallback(async () => {
        if (isNameField) {
            const isValid = await form.trigger("internalNotes");
            if (!isValid) return;
        }

        const data = form.getValues();
        const val = data?.internalNotes ?? internalNotes;
        let params = {};

        if (title === "First Name") {
            params = {
                firstName: val?.trim(),
                _id: driverId,
            };
        } else if (title === "Last Name") {
            params = {
                lastName: val?.trim(),
                _id: driverId,
            };
        } else if (title === "Address") {
            params = {
                address: val,
                _id: driverId,
            };
        } else if (title === "City") {
            params = {
                city: val,
                _id: driverId,
            };
        } else if (title === "Zip Code") {
            const res = zipCodeValidation(val);
            if (res) {
                params = {
                    zipCode: val,
                    _id: driverId,
                };
            } else {
                return alert("Please enter valid zip code ");
            }
        } else if (title === "Notes") {
            params = {
                internalNote: val,
                _id: driverId,
            };
        }

        const result = await updateDriverMetro(params);
        if (!result?.error) {
            sendSuccessMessage(`${title} updated successfully`);
            actions.refetch.setIsDataRefetch(true);
        } else {
            checkForError(result);
        }
        actions.modal.closeNotes();
    }, [
        driverId,
        updateDriverMetro,
        title,
        isNameField,
        internalNotes,
        form,
        zipCodeValidation,
    ]);

    const onCancel = useCallback(() => {
        if (
            !form.formState.isDirty ||
            window.confirm("Do you want to close the dialog? Changes will be lost.")
        ) {
            actions.modal.closeNotes();
        }
    }, [form.formState.isDirty]);

    const isSubmitDisabled =
        isUpdatingDriver ||
        initialNotes.trim() === internalNotes.trim() ||
        (isNameField && (Boolean(form.formState.errors?.internalNotes) || !internalNotes.trim()));

    return (
        <Modal
            open
            onClose={onCancel}
            aria-labelledby="modal-modal-title"
            aria-describedby="modal-modal-description"
        >
            <div className={`modal after-loading`} style={{ maxWidth: "35.875rem" }}>
                <div className="modal__header">
                    <h2 className="modal__title">{`Update ${title}`}</h2>
                </div>
                <div className="modal__body">
                    <InputItem
                        title={`${title}`}
                        name="internalNotes"
                        form={form}
                        type="text"
                        initialValue={initialNotes}
                        onInput={(e) => setInternalNotes(e?.target?.value)}
                    />
                </div>
                <div className="modal__footer">
                    <button className="btn" onClick={onCancel}>
                        Cancel
                    </button>
                    <div style={{ position: 'relative' }}>
                        <button
                            className="btn btn_accent"
                            disabled={isSubmitDisabled}
                            onClick={onNotesSubmit}
                        >
                            OK
                        </button>
                        {isUpdatingDriver && (
                            <CircularProgress size={18}
                                sx={{
                                color: '#fff',
                                position: 'absolute',
                                top: '50%',
                                left: '83%',
                                marginTop: '-8px',
                                marginLeft: '-10px',
                            }} />
                        )}
                    </div>
                </div>
            </div>
        </Modal>
    );
};

export default NotesModal;
